/**
 * Distribution Stats Components
 *
 * Comprehensive analytics visualizations for user engagement:
 * - Vote distribution histogram
 * - Interactive threshold explorers
 * - Model exposure distribution
 * - Activity span distribution
 * - Model exposure progression
 */

import { useCallback, useEffect, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

// Types
interface DistributionBucket {
  bucket: string;
  count: number;
  percentage: number;
}

interface ThresholdStat {
  threshold: number;
  count: number;
  percentage: number;
}

interface ModelExposureProgressionPoint {
  atVote: number;
  avgModelsSeen: number;
  usersAtThisPoint: number;
}

interface DistributionStatsData {
  voteDistribution: DistributionBucket[];
  battleThresholds: ThresholdStat[];
  modelExposure: {
    distribution: DistributionBucket[];
    avgModelsSeen: number;
    totalActiveModels: number;
    usersSeenAllModels: number;
  };
  activitySpan: DistributionBucket[];
  modelExposureProgression: ModelExposureProgressionPoint[];
  totals: {
    totalUsers: number;
    totalVotes: number;
  };
}

interface CustomThresholdData {
  votesThreshold?: {
    threshold: number;
    count: number;
    percentage: number;
  };
  modelsThreshold?: {
    threshold: number;
    count: number;
    percentage: number;
  };
  daysActiveThreshold?: {
    threshold: number;
    count: number;
    percentage: number;
  };
  atVoteExposure?: {
    atVote: number;
    avgModelsSeen: number;
    usersAtThisPoint: number;
  };
  totalUsers: number;
}

// Horizontal bar chart component
function HorizontalBarChart({
  data,
  title,
  description,
  accentColor = '#8B2332',
}: {
  data: DistributionBucket[];
  title: string;
  description?: string;
  accentColor?: string;
}) {
  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
      <h3 className="text-lg font-semibold text-text mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-text-light mb-4">{description}</p>
      )}
      <div className="space-y-3">
        {data.map(item => (
          <div key={item.bucket} className="flex items-center gap-3">
            <div className="w-20 text-sm text-text-light font-medium text-right">
              {item.bucket}
            </div>
            <div className="flex-1 h-6 bg-cream rounded overflow-hidden">
              <div
                className="h-full rounded transition-all duration-300"
                style={{
                  width: `${(item.count / maxCount) * 100}%`,
                  backgroundColor: accentColor,
                  minWidth: item.count > 0 ? '4px' : '0',
                }}
              />
            </div>
            <div className="w-24 text-sm text-text">
              <span className="font-semibold">{item.count}</span>
              <span className="text-text-light ml-1">({item.percentage}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Interactive threshold explorer
function ThresholdExplorer({
  label,
  placeholder,
  value,
  onChange,
  result,
  totalUsers,
  unit,
  loading,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  result?: { count: number; percentage: number } | null;
  totalUsers: number;
  unit: string;
  loading?: boolean;
}) {
  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-sm text-text-light">{label}</span>
        <input
          type="number"
          min="1"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-20 px-2 py-1 border border-[#D1C9C4] rounded text-center text-sm focus:outline-none focus:border-accent"
        />
        <span className="text-sm text-text-light">{unit}</span>
      </div>
      <div className="text-center">
        {loading ? (
          <div className="text-text-light text-sm">Loading...</div>
        ) : result ? (
          <>
            <div
              className="text-3xl font-semibold"
              style={{ color: '#8B2332' }}
            >
              {result.percentage}%
            </div>
            <div className="text-sm text-text-light">
              {result.count.toLocaleString()} of {totalUsers.toLocaleString()}{' '}
              users
            </div>
          </>
        ) : value ? (
          <div className="text-text-light text-sm">
            Enter a value and press Enter
          </div>
        ) : (
          <div className="text-text-light text-sm">—</div>
        )}
      </div>
    </div>
  );
}

// Model exposure at vote N explorer
function VoteExposureExplorer({
  value,
  onChange,
  result,
  loading,
}: {
  value: string;
  onChange: (value: string) => void;
  result?: {
    atVote: number;
    avgModelsSeen: number;
    usersAtThisPoint: number;
  } | null;
  loading?: boolean;
}) {
  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
      <h3 className="text-lg font-semibold text-text mb-1">
        Model Coverage by Vote Count
      </h3>
      <p className="text-sm text-text-light mb-3">
        How diverse is user experience early on? This shows the average number
        of unique models a user had been exposed to by the time they reached a
        specific vote count.
      </p>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-text-light">By vote #</span>
        <input
          type="number"
          min="1"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="10"
          className="w-20 px-2 py-1 border border-[#D1C9C4] rounded text-center text-sm focus:outline-none focus:border-accent"
        />
        <span className="text-sm text-text-light">
          how many models had users seen?
        </span>
      </div>
      <div className="text-center">
        {loading ? (
          <div className="text-text-light text-sm">Loading...</div>
        ) : result ? (
          <>
            <div
              className="text-3xl font-semibold"
              style={{ color: '#8B2332' }}
            >
              {result.avgModelsSeen}
            </div>
            <div className="text-sm text-text-light">
              avg models seen ({result.usersAtThisPoint} users reached vote{' '}
              {result.atVote})
            </div>
          </>
        ) : value ? (
          <div className="text-text-light text-sm">
            No users have reached this vote count
          </div>
        ) : (
          <div className="text-text-light text-sm">
            Enter a vote number above
          </div>
        )}
      </div>
    </div>
  );
}

// Model exposure summary card
function ModelExposureSummary({
  avgModelsSeen,
  totalActiveModels,
  usersSeenAllModels,
}: {
  avgModelsSeen: number;
  totalActiveModels: number;
  usersSeenAllModels: number;
}) {
  return (
    <div className="flex gap-4 mt-4 pt-4 border-t border-[#E8E3DD]">
      <div className="text-center flex-1">
        <div className="text-lg font-semibold text-text">{avgModelsSeen}</div>
        <div className="text-xs text-text-light">Avg models seen</div>
      </div>
      <div className="text-center flex-1">
        <div className="text-lg font-semibold text-text">
          {totalActiveModels}
        </div>
        <div className="text-xs text-text-light">Active models</div>
      </div>
      <div className="text-center flex-1">
        <div className="text-lg font-semibold" style={{ color: '#8B2332' }}>
          {usersSeenAllModels}
        </div>
        <div className="text-xs text-text-light">Seen all models</div>
      </div>
    </div>
  );
}

// Main component that fetches and displays all stats
export function DistributionStats() {
  const [data, setData] = useState<DistributionStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Interactive threshold inputs
  const [minVotesInput, setMinVotesInput] = useState('10');
  const [minModelsInput, setMinModelsInput] = useState('5');
  const [minDaysInput, setMinDaysInput] = useState('7');
  const [atVoteInput, setAtVoteInput] = useState('10');

  // Custom threshold results
  const [customData, setCustomData] = useState<CustomThresholdData | null>(
    null
  );
  const [customLoading, setCustomLoading] = useState(false);

  // Debounced values
  const [debouncedValues, setDebouncedValues] = useState({
    minVotes: minVotesInput,
    minModels: minModelsInput,
    minDays: minDaysInput,
    atVote: atVoteInput,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValues({
        minVotes: minVotesInput,
        minModels: minModelsInput,
        minDays: minDaysInput,
        atVote: atVoteInput,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [minVotesInput, minModelsInput, minDaysInput, atVoteInput]);

  // Load base distribution data
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const response = await apiRequest('/api/admin/distribution-stats');
        setData(response as DistributionStatsData);
      } catch (e) {
        logError(
          'Failed to load distribution stats',
          e instanceof Error ? e : undefined
        );
        setError(e instanceof Error ? e.message : 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Load custom thresholds
  const loadCustomThresholds = useCallback(async () => {
    const params = new URLSearchParams();

    if (debouncedValues.minVotes) {
      params.set('minVotes', debouncedValues.minVotes);
    }
    if (debouncedValues.minModels) {
      params.set('minModels', debouncedValues.minModels);
    }
    if (debouncedValues.minDays) {
      params.set('minDaysActive', debouncedValues.minDays);
    }
    if (debouncedValues.atVote) {
      params.set('atVoteNumber', debouncedValues.atVote);
    }

    if (params.toString()) {
      try {
        setCustomLoading(true);
        const response = await apiRequest(
          `/api/admin/custom-thresholds?${params}`
        );
        setCustomData(response as CustomThresholdData);
      } catch (e) {
        logError(
          'Failed to load custom thresholds',
          e instanceof Error ? e : undefined
        );
      } finally {
        setCustomLoading(false);
      }
    }
  }, [debouncedValues]);

  useEffect(() => {
    loadCustomThresholds();
  }, [loadCustomThresholds]);

  if (loading) {
    return (
      <div className="text-center text-text-light py-8">
        Loading distribution statistics...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Vote Distribution Histogram */}
      <HorizontalBarChart
        data={data.voteDistribution}
        title="Vote Distribution (Votes per User)"
        description="How many votes has each user submitted? Groups users by their total vote count to show engagement skew."
        accentColor="#8B2332"
      />

      {/* Interactive Threshold Explorers */}
      <div>
        <h3 className="text-lg font-semibold text-text mb-1">
          User Engagement Thresholds
        </h3>
        <p className="text-sm text-text-light mb-4">
          Explore how many users reach specific engagement milestones. Useful
          for understanding sample size at different filter levels.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ThresholdExplorer
            label="Users with ≥"
            placeholder="10"
            value={minVotesInput}
            onChange={setMinVotesInput}
            result={customData?.votesThreshold}
            totalUsers={customData?.totalUsers || data.totals.totalUsers}
            unit="votes"
            loading={customLoading}
          />
          <ThresholdExplorer
            label="Users who've seen ≥"
            placeholder="5"
            value={minModelsInput}
            onChange={setMinModelsInput}
            result={customData?.modelsThreshold}
            totalUsers={customData?.totalUsers || data.totals.totalUsers}
            unit="models"
            loading={customLoading}
          />
          <ThresholdExplorer
            label="Users active ≥"
            placeholder="7"
            value={minDaysInput}
            onChange={setMinDaysInput}
            result={customData?.daysActiveThreshold}
            totalUsers={customData?.totalUsers || data.totals.totalUsers}
            unit="days"
            loading={customLoading}
          />
        </div>
      </div>

      {/* Two-column layout for Model Exposure and Activity Span */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Exposure Distribution */}
        <div>
          <HorizontalBarChart
            data={data.modelExposure.distribution}
            title="Model Coverage per User"
            description="How many unique models has each user been exposed to? Measures the breadth of model comparisons each user has seen."
            accentColor="#2563EB"
          />
          <div className="bg-white border border-[#D1C9C4] rounded-lg p-4 mt-[-1px] rounded-t-none border-t-0">
            <ModelExposureSummary
              avgModelsSeen={data.modelExposure.avgModelsSeen}
              totalActiveModels={data.modelExposure.totalActiveModels}
              usersSeenAllModels={data.modelExposure.usersSeenAllModels}
            />
          </div>
        </div>

        {/* Activity Span Distribution */}
        <HorizontalBarChart
          data={data.activitySpan}
          title="User Engagement Duration"
          description="How long are users active? Measures the time span between a user's first and last vote to understand retention."
          accentColor="#059669"
        />
      </div>

      {/* Model Exposure at Vote N - Interactive */}
      <VoteExposureExplorer
        value={atVoteInput}
        onChange={setAtVoteInput}
        result={customData?.atVoteExposure}
        loading={customLoading}
      />
    </div>
  );
}
